import React from 'react';
import type { 
  AgeLevel, ExperienceLevel, Sport,
  FootballOffensiveScheme, FootballDefensiveScheme,
  BasketballOffensiveScheme, BasketballDefensiveScheme,
  SoccerFormation, SoccerStyle,
  BaseballFocus,
  HockeySystem,
  VolleyballOffense, VolleyballDefense,
  LacrosseOffense, LacrosseDefense
} from '../../types';
import { SportFormats } from '../../types';
import { 
  FOOTBALL_OFFENSIVE_FORMATIONS, 
  FOOTBALL_DEFENSIVE_FORMATIONS,
  SOCCER_FORMATIONS
} from '../../data/sportsFormations';

interface SettingsFormState {
  name: string;
  sport: Sport;
  ageLevel: AgeLevel;
  experienceLevel: ExperienceLevel;
  rosterSize: number;
  format: string;
  offensiveScheme?: string;
  defensiveScheme?: string;
  innings?: number; // For baseball/softball
}

interface TeamInformationSectionProps {
  formState: SettingsFormState;
  onInputChange: (field: keyof SettingsFormState, value: string | number) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const TeamInformationSection: React.FC<TeamInformationSectionProps> = ({
  formState,
  onInputChange,
  onSubmit
}) => {
  // Form options
  const ageLevels: AgeLevel[] = [
    '8U', '9U', '10U', '11U', '12U', '13U', '14U'
  ];
  const experienceLevels: ExperienceLevel[] = ['Beginner', 'Intermediate', 'Advanced'];
  
  // Football schemes - use actual formation names
  const footballOffensiveSchemes = FOOTBALL_OFFENSIVE_FORMATIONS.map(formation => formation.name).sort();
  const footballDefensiveSchemes = FOOTBALL_DEFENSIVE_FORMATIONS.map(formation => formation.name).sort();

  // Basketball schemes
  const basketballOffensiveSchemes: BasketballOffensiveScheme[] = [
    'Motion', 'Princeton', 'Triangle', 'Pick and Roll', 'Fast Break', 'Flex', 'Zone'
  ];
  const basketballDefensiveSchemes: BasketballDefensiveScheme[] = [
    'Man-to-Man', '2-3 Zone', '3-2 Zone', '1-3-1 Zone', 'Box and One', 'Full Court Press', 'Half Court Press'
  ];

  // Soccer schemes - use actual formation names
  const soccerFormations = SOCCER_FORMATIONS.map(formation => formation.name).sort();
  const soccerStyles: SoccerStyle[] = [
    'Possession', 'Counter-Attack', 'High Press', 'Tiki-Taka', 'Direct', 'Total Football', 'Park the Bus'
  ];

  // Baseball/Softball focuses
  const baseballFocuses: BaseballFocus[] = [
    'Small Ball', 'Power Hitting', 'Defensive', 'Balanced', 'Pitching Focus', 'Speed Focus'
  ];

  // Hockey systems
  const hockeySystems: HockeySystem[] = [
    'Dump and Chase', 'Neutral Zone Trap', 'Left Wing Lock', '1-2-2 Forecheck', '2-1-2 Forecheck'
  ];

  // Volleyball schemes
  const volleyballOffenses: VolleyballOffense[] = [
    '5-1', '6-2', '4-2', 'International', 'West Coast'
  ];
  const volleyballDefenses: VolleyballDefense[] = [
    'Rotation', 'Perimeter', 'Man-Up', '6-Up', 'Base'
  ];

  // Lacrosse schemes
  const lacrosseOffenses: LacrosseOffense[] = [
    'Motion', '1-4-1', '2-3-1', '3-3', 'Wheel'
  ];
  const lacrosseDefenses: LacrosseDefense[] = [
    'Man-to-Man', 'Zone', 'Backer', 'Slide Package', 'Crease Defense'
  ];

  // Render sport-specific fields based on the team's sport
  const renderSportSpecificFields = () => {
    switch (formState.sport) {
      case 'Football':
        return (
          <>
            <div>
              <label className="form-label">
                Offensive Scheme
              </label>
              <select
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(e) => onInputChange('offensiveScheme', e.target.value)}
                required
              >
                <option value="">Select Offensive Scheme</option>
                {footballOffensiveSchemes.map((scheme) => (
                  <option key={scheme} value={scheme}>{scheme}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">
                Defensive Scheme
              </label>
              <select
                className="select"
                value={formState.defensiveScheme || ''}
                onChange={(e) => onInputChange('defensiveScheme', e.target.value)}
                required
              >
                <option value="">Select Defensive Scheme</option>
                {footballDefensiveSchemes.map((scheme) => (
                  <option key={scheme} value={scheme}>{scheme}</option>
                ))}
              </select>
            </div>
          </>
        );
        
      case 'Basketball':
        return (
          <>
            <div>
              <label className="form-label">
                Offensive Scheme
              </label>
              <select
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(e) => onInputChange('offensiveScheme', e.target.value)}
                required
              >
                <option value="">Select Offensive Scheme</option>
                {basketballOffensiveSchemes.map((scheme) => (
                  <option key={scheme} value={scheme}>{scheme}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">
                Defensive Scheme
              </label>
              <select
                className="select"
                value={formState.defensiveScheme || ''}
                onChange={(e) => onInputChange('defensiveScheme', e.target.value)}
                required
              >
                <option value="">Select Defensive Scheme</option>
                {basketballDefensiveSchemes.map((scheme) => (
                  <option key={scheme} value={scheme}>{scheme}</option>
                ))}
              </select>
            </div>
          </>
        );
        
      case 'Soccer':
        return (
          <>
            <div>
              <label className="form-label">
                Formation
              </label>
              <select
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(e) => onInputChange('offensiveScheme', e.target.value)}
                required
              >
                <option value="">Select Formation</option>
                {soccerFormations.map((formation) => (
                  <option key={formation} value={formation}>{formation}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">
                Playing Style
              </label>
              <select
                className="select"
                value={formState.defensiveScheme || ''}
                onChange={(e) => onInputChange('defensiveScheme', e.target.value)}
                required
              >
                <option value="">Select Playing Style</option>
                {soccerStyles.map((style) => (
                  <option key={style} value={style}>{style}</option>
                ))}
              </select>
            </div>
          </>
        );
        
      case 'Baseball':
      case 'Softball':
        return (
          <>
            <div>
              <label className="form-label">
                Team Focus
              </label>
              <select
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(e) => onInputChange('offensiveScheme', e.target.value)}
                required
              >
                <option value="">Select Team Focus</option>
                {baseballFocuses.map((focus) => (
                  <option key={focus} value={focus}>{focus}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">
                Number of Innings
              </label>
              <select
                className="select"
                value={formState.innings || 7}
                onChange={(e) => onInputChange('innings', parseInt(e.target.value))}
                required
              >
                <option value="4">4 Innings (T-Ball)</option>
                <option value="5">5 Innings (Coach Pitch)</option>
                <option value="6">6 Innings (Youth)</option>
                <option value="7">7 Innings (Standard)</option>
                <option value="9">9 Innings (High School/Adult)</option>
              </select>
              <p className="text-xs text-gray-600 mt-1">
                Select the number of innings your league plays. This affects depth chart randomization.
              </p>
            </div>
          </>
        );
        
      case 'Hockey':
        return (
          <div>
            <label className="form-label">
              Team System
            </label>
            <select
              className="select"
              value={formState.offensiveScheme || ''}
              onChange={(e) => onInputChange('offensiveScheme', e.target.value)}
              required
            >
              <option value="">Select Team System</option>
              {hockeySystems.map((system) => (
                <option key={system} value={system}>{system}</option>
              ))}
            </select>
          </div>
        );
        
      case 'Volleyball':
        return (
          <>
            <div>
              <label className="form-label">
                Offensive System
              </label>
              <select
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(e) => onInputChange('offensiveScheme', e.target.value)}
                required
              >
                <option value="">Select Offensive System</option>
                {volleyballOffenses.map((offense) => (
                  <option key={offense} value={offense}>{offense}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">
                Defensive System
              </label>
              <select
                className="select"
                value={formState.defensiveScheme || ''}
                onChange={(e) => onInputChange('defensiveScheme', e.target.value)}
                required
              >
                <option value="">Select Defensive System</option>
                {volleyballDefenses.map((defense) => (
                  <option key={defense} value={defense}>{defense}</option>
                ))}
              </select>
            </div>
          </>
        );
        
      case 'Lacrosse':
        return (
          <>
            <div>
              <label className="form-label">
                Offensive System
              </label>
              <select
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(e) => onInputChange('offensiveScheme', e.target.value)}
                required
              >
                <option value="">Select Offensive System</option>
                {lacrosseOffenses.map((offense) => (
                  <option key={offense} value={offense}>{offense}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">
                Defensive System
              </label>
              <select
                className="select"
                value={formState.defensiveScheme || ''}
                onChange={(e) => onInputChange('defensiveScheme', e.target.value)}
                required
              >
                <option value="">Select Defensive System</option>
                {lacrosseDefenses.map((defense) => (
                  <option key={defense} value={defense}>{defense}</option>
                ))}
              </select>
            </div>
          </>
        );
        
      default:
        // For backward compatibility
        return (
          <>
            <div>
              <label className="form-label">
                Offensive Scheme
              </label>
              <select
                className="select"
                value={formState.offensiveScheme || ''}
                onChange={(e) => onInputChange('offensiveScheme', e.target.value)}
                required
              >
                <option value="">Select Offensive Scheme</option>
                {footballOffensiveSchemes.map((scheme) => (
                  <option key={scheme} value={scheme}>{scheme}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">
                Defensive Scheme
              </label>
              <select
                className="select"
                value={formState.defensiveScheme || ''}
                onChange={(e) => onInputChange('defensiveScheme', e.target.value)}
                required
              >
                <option value="">Select Defensive Scheme</option>
                {footballDefensiveSchemes.map((scheme) => (
                  <option key={scheme} value={scheme}>{scheme}</option>
                ))}
              </select>
            </div>
          </>
        );
    }
  };

  return (
    <>
      <form onSubmit={onSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="form-label">
              Team Name
            </label>
            <input
              type="text"
              className="input"
              value={formState.name}
              onChange={(e) => onInputChange('name', e.target.value)}
              required
            />
          </div>

          <div>
            <label className="form-label">
              Sport
            </label>
            <div className="w-full px-3 py-2 border border-stone bg-cream rounded-xl text-dark font-medium">
              {formState.sport}
            </div>
          </div>

          <div>
            <label className="form-label">
              Format
            </label>
            <select
              className="select"
              value={formState.format}
              onChange={(e) => onInputChange('format', e.target.value)}
              required
            >
              <option value="">Select Format</option>
              {SportFormats[formState.sport].map((format) => (
                <option key={format} value={format}>{format}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">
              Roster Size
            </label>
            <input
              type="number"
              min="1"
              className="input"
              value={formState.rosterSize}
              onChange={(e) => onInputChange('rosterSize', parseInt(e.target.value))}
              required
            />
          </div>

          <div>
            <label className="form-label">
              Age Level
            </label>
            <select
              className="select"
              value={formState.ageLevel}
              onChange={(e) => onInputChange('ageLevel', e.target.value as AgeLevel)}
              required
            >
              <option value="">Select Age Level</option>
              {ageLevels.map((level) => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">
              Experience Level
            </label>
            <select
              className="select"
              value={formState.experienceLevel}
              onChange={(e) => onInputChange('experienceLevel', e.target.value as ExperienceLevel)}
              required
            >
              <option value="">Select Experience Level</option>
              {experienceLevels.map((level) => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          {/* Render sport-specific fields */}
          {renderSportSpecificFields()}
        </div>
      </form>
    </>
  );
}; 